import type { OnInit } from '@angular/core';
import { Component, HostListener, Inject, Input, PLATFORM_ID, inject, signal } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { LazyImgDirective } from '@core-mkt/services/lazy-load/lazy-load.directive';
import { Image } from '@core-mkt/interfaces/image';
import { StorageService } from '@core-mkt/services/storage/storage.service';
import { QueryParamDirective } from '@core-mkt/services/query-param/query-param.directive';
import type { MenuItem, SimplifiedNav, StateList } from '../../header/header';
import { Menu } from '../../header/header';
import { headerData, MenuGroup, newHeaderData, specificPageHeaderData } from '../../header/header-data';
import { stateList } from '../../header/state-nav-data';
import {
  DEC_STATE_STORAGE_KEY,
  buildSimplifiedNav,
  displayNameForSlug,
  slugFromHref,
} from '../../header/simplified-nav';
import { MobileMenuComponent } from '../mobile-menu/mobile-menu.component';

const GLOBAL_STATE_LABEL = 'Select State';

@Component({
  selector: 'dec-desktop-menu',
  templateUrl: './desktop-menu.component.html',
  styleUrls: ['./desktop-menu.component.scss'],
  standalone: true,
  imports: [CommonModule, MobileMenuComponent, LazyImgDirective, QueryParamDirective],
})
export class DesktopMenuComponent implements OnInit {
  @Input() data: Menu;
  @Input() secondaryLogo: Image;
  @Input() _showLogoHref = true;

  private readonly storage = inject(StorageService);
  private readonly isBrowser: boolean;

  _menuGroups: MenuItem[][];
  currentMenu: Menu;
  showMenu = false;

  /** Reactive presentation state for the simplified nav (drives the template). */
  simplifiedNav = signal<SimplifiedNav | null>(null);
  stateLabel = signal<string>(GLOBAL_STATE_LABEL);
  /** Currently-open dropdown key ('driversEd' | 'more' | 'state'), or null when all closed. */
  openDropdown = signal<string | null>(null);

  get stateSelectionList(): StateList[] {
    return stateList;
  }

  get showLogoHref(): boolean {
    return this._showLogoHref;
  }

  get isGlobalPage(): boolean {
    return this.data.state === GLOBAL_STATE_LABEL;
  }

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    // Server value (from Craft pageState) is the source of truth on first paint.
    const serverState = this.data.state;
    const isGlobal = serverState === GLOBAL_STATE_LABEL;
    this.resolveMenu(serverState);

    // Progressive enhancement: on global/default pages a previously-selected
    // state (localStorage) wins. State pages keep their URL-resolved nav and
    // never read/override storage. SSR first-paints the global menu; this runs
    // client-side only.
    if (this.isBrowser && isGlobal) {
      const storedDisplay = displayNameForSlug(this.storage.get(DEC_STATE_STORAGE_KEY));
      if (storedDisplay) {
        this.resolveMenu(storedDisplay);
      }
    }

    this.refreshSimplifiedNav();
  }

  /** (Re)build the menu groups for a given state display name using the existing per-state data pipeline. */
  private resolveMenu(state: string): void {
    this.currentMenu = this.data;
    this.currentMenu.state = state;
    this.currentMenu.menuItems = [];
    if (state === GLOBAL_STATE_LABEL) {
      this.currentMenu.menuItems = [...headerData];
      this.formatMenuGroups();
    } else {
      this.setMenuForState();
    }
  }

  private refreshSimplifiedNav(): void {
    this.simplifiedNav.set(buildSimplifiedNav(this._menuGroups));
    this.stateLabel.set(this.currentMenu.state);
  }

  /** Toggle a single dropdown; closing any other that was open. */
  toggleDropdown(key: string, event: Event): void {
    event.stopPropagation();
    this.openDropdown.update((current) => (current === key ? null : key));
  }

  /**
   * Persist an explicit state selection before the anchor navigates, so the
   * choice sticks on subsequent global pages. Navigation proceeds normally.
   */
  selectState(item: StateList): void {
    if (this.isBrowser) {
      this.storage.set(DEC_STATE_STORAGE_KEY, slugFromHref(item.href));
    }
  }

  @HostListener('document:click')
  closeDropdowns(): void {
    this.openDropdown.set(null);
  }

  /** Close any open dropdown on Escape for keyboard users. */
  @HostListener('document:keydown.escape')
  closeOnEscape(): void {
    this.openDropdown.set(null);
  }

  getLinkForGroup(groupEntry: MenuItem): string {
    const removeDmvState = ['alaska', 'arkansas'];
    const lowerCaseDataState = this.data.state.toLowerCase();
    switch (groupEntry.text as MenuGroup) {
      case MenuGroup.DriversEd:
      case MenuGroup.Teens:
      case MenuGroup.Adults:
      case MenuGroup.PracticePermitTest:
        return this.isGlobalPage
          ? groupEntry.href
          : `/${lowerCaseDataState.replace(/ /g, '-').replace(/\./g, '')}${groupEntry.href}`;
      case MenuGroup.DMVInformation:
        return this.isGlobalPage || removeDmvState.includes(lowerCaseDataState)
          ? groupEntry.href
          : `${groupEntry.href}${lowerCaseDataState.replace(/ /g, '-').replace(/\./g, '')}`;
      default:
        return groupEntry.href;
    }
  }

  setMenuForState(): void {
    this.currentMenu.state = this.data.state;
    const currentState: string = this.currentMenu.state.toLowerCase().replace(/ /g, '-').replace(/\./g, '');
    const stateMenu = newHeaderData[currentState];
    const targetPageMenu = specificPageHeaderData[this.currentMenu.slug];
    const removeTrafficSchool = [
      'alabama',
      'arkansas',
      'arizona',
      'connecticut',
      'hawaii',
      'indiana',
      'iowa',
      'kentucky',
      'maine',
      'maryland',
      'massachusetts',
      'michigan',
      'mississippi',
      'montana',
      'new-hampshire',
      'oregon',
      'pennsylvania',
      'rhode-island',
      'south-dakota',
      'texas',
      'virginia',
      'washington-dc',
      'west-virginia',
      'wyoming',
    ];
    const removeDefensiveDriving = ['arizona'];
    if (targetPageMenu) {
      this.currentMenu.menuItems = targetPageMenu.menuItems;
    } else {
      if (stateMenu && stateMenu.dmvLabel) {
        this.currentMenu.dmvLabel = stateMenu.dmvLabel;
      }
      if (stateMenu && stateMenu.reWriteDefault) {
        this.currentMenu.menuItems = stateMenu.menuItems;
      } else {
        for (const menuItem of headerData) {
          const newMenuItem = { ...menuItem };
          //only get link for group if menu item does not exist in the stateMenu menuItems array.
          if ((stateMenu && !stateMenu.menuItems.find((item) => item.text === menuItem.text)) || !stateMenu) {
            newMenuItem.href = this.getLinkForGroup(menuItem);
          }
          if (menuItem.text === 'DMV Information' && stateMenu && stateMenu.dmvLabel) {
            newMenuItem.text = stateMenu.dmvLabel;
            newMenuItem.href = newMenuItem.href.endsWith('/') ? newMenuItem.href : newMenuItem.href + '/';
          }
          if (
            (menuItem.text === 'Traffic School' && !removeTrafficSchool.includes(currentState)) ||
            (menuItem.text === 'In-Car Driving Lessons' && this.currentMenu.showInPersonLessons) ||
            (menuItem.text !== 'Traffic School' && menuItem.text !== 'In-Car Driving Lessons')
          ) {
            this.currentMenu.menuItems.push(newMenuItem);
          }
        }
        //if there are menu items for the state add them to the menuList based on the order field of the menu item in the stateMenu menuItems array.
        if (stateMenu && stateMenu.menuItems.length > 0) {
          for (const menuItem of stateMenu.menuItems) {
            const matchingMenuItemIdx = this.currentMenu.menuItems.findIndex((item) => item.text === menuItem.text);
            //skip to next iteration of the for loop if page has turned off show in person lessons and the menu item is 'In-Car Driving Lessons'.
            if (menuItem.text === 'In-Car Driving Lessons' && !this.currentMenu.showInPersonLessons) {
              continue;
            }
            if (matchingMenuItemIdx !== -1) {
              this.currentMenu.menuItems[matchingMenuItemIdx].href = menuItem.href;
            } else {
              this.currentMenu.menuItems.splice(menuItem.order, 0, menuItem);
            }

            // Remove 'Defensive Driving' item for states in the removeDefensiveDriving array

            if (menuItem.text === 'Defensive Driving' && removeDefensiveDriving.includes(currentState)) {
              this.currentMenu.menuItems = this.currentMenu.menuItems.filter(
                (item) => !item.text.includes('Defensive Driving'),
              );
            }
          }
        }
      }
    }
    this.formatMenuGroups();
  }

  formatMenuGroups(): void {
    const menuGroupList: MenuItem[][] = [];
    let groupIndex = -1;
    // Build the list of menu groups by pushing to the same index until a new position of "top" is seen;
    // until "top" is seen, the menu item is assumed to be a sub-item ("bottom") so is pushed to the same
    // group. Once a new "top" is seen, a new group gets formed at the following index and any sub-items
    // will be pushed there, etc., until the whole array has been iterated over.
    for (let i = 0; i < this.currentMenu.menuItems.length; i++) {
      if (this.currentMenu.menuItems[i].position === 'top') {
        groupIndex++;
        menuGroupList[groupIndex] = [];
      }
      menuGroupList[groupIndex].push(this.currentMenu.menuItems[i]);
    }
    this._menuGroups = menuGroupList;
    this.showMenu = true;
  }
}
